export type ProfileRow = {
  id: string;
  email: string | null;
  full_name?: string | null;
  display_name?: string | null;
  email_domain: string | null;
  access_status: string | null;
  primary_organization_id: string | null;
  created_at?: string | null;
  last_sign_in_at?: string | null;
  blocked_reason?: string | null;
};

export type MembershipRow = {
  user_id: string;
  organization_id: string;
  role: string;
  is_active: boolean;
};

export type MemberWithProfile = {
  user_id: string;
  organization_id: string;
  roles: string[];
  email: string | null;
  full_name: string | null;
  display_name: string | null;
  last_sign_in_at: string | null;
};

export const ROLE_OPTIONS = [
  "sales",
  "sales_rep",
  "ops_reviewer",
  "admin",
  "owner",
  "trainer",
  "marketing",
  "ads_guidance_editor",
  "dev",
  /** Manufacturing Suite plant access; assign only via platform_owner / platform_admin (see SQL guard). */
  "manufacturing",
  "manufacturing_plant",
  "manufacturing_plant_supervisor",
  "manufacturing_plant_manager",
  "manufacturing_purchasing",
  "mfg_qc",
  "mfg_final_qc",
  "manufacturing_repair_technician",
] as const;
export type OrgRole = (typeof ROLE_OPTIONS)[number];

export function orgRolesForAddDropdown(canAssignManufacturingOrgRole: boolean): OrgRole[] {
  return ROLE_OPTIONS.filter(
    (r) =>
      (r !== "manufacturing" &&
        r !== "manufacturing_plant" &&
        r !== "manufacturing_plant_supervisor" &&
        r !== "manufacturing_plant_manager" &&
        r !== "manufacturing_purchasing" &&
        r !== "mfg_qc" &&
        r !== "mfg_final_qc") ||
      canAssignManufacturingOrgRole
  );
}

/** Short labels for org-role dropdowns (additive roles; assign several per user as needed). */
export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  sales: "Sales (full Sales bucket)",
  sales_rep: "Sales rep (intake + leads; narrow Sales seat)",
  ops_reviewer: "Ops reviewer (intake review + Ops)",
  admin: "Admin",
  owner: "Owner",
  trainer: "Trainer",
  marketing: "Marketing",
  ads_guidance_editor: "Ads guidance editor",
  dev: "Dev",
  manufacturing: "Manufacturing (desk)",
  manufacturing_plant: "Manufacturing plant (floor; needs phase:… tags)",
  manufacturing_plant_supervisor: "Manufacturing plant supervisor (all phases)",
  manufacturing_plant_manager: "Manufacturing plant manager (Production Audits + Inventory count; no desk admin)",
  manufacturing_purchasing: "Manufacturing purchasing (full Inventory + Models)",
  mfg_qc: "Manufacturing QC (Builder Assistant and open QC inbox)",
  mfg_final_qc: "Final QC (sign off a finished inspection, or send items back)",
  manufacturing_repair_technician: "Manufacturing repair technician (Service materials usage)",
};

export function labelOrgRole(role: string): string {
  return role in ORG_ROLE_LABELS ? ORG_ROLE_LABELS[role as OrgRole] : role;
}

/** Platform role dropdown options (empty string = none). */
export const PLATFORM_ROLE_OPTIONS = ["", "platform_admin", "platform_dev"] as const;
export type PlatformRole = (typeof PLATFORM_ROLE_OPTIONS)[number];

export function formatAdminProfileName(p: ProfileRow) {
  return (
    p.full_name?.trim() ||
    p.display_name?.trim() ||
    (p.email ? p.email.split("@")[0] : "Unknown")
  );
}

export function formatMemberName(m: MemberWithProfile) {
  return (
    m.full_name?.trim() ||
    m.display_name?.trim() ||
    (m.email ? m.email.split("@")[0] : "Unknown")
  );
}
